import {
  useApplyCardsSettingsMutation,
  useGetCardsQuery,
  useResetCardIncomeMutation,
  useResetCardsIncomeMutation,
  useToggleAllCardsMutation,
} from '../../../../../../../redux/services/CardsApi';
import mapCards from '../../../../../../../utils/functions/map/mapCards';
import { Loading } from '../../../../../../../components/Loading/Loading';
import { type CardsModel } from '../../../../../../../utils/types';
import { Blocks } from '../../../../../../../components/Blocks/Blocks';
import { EditCardModal } from '../components/EditCardModal/EditCardModal';
import { type TCardsContentProps } from '../../CardsContent';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Pagination } from '../../../../../../../components/Pagination/Pagination';
import { pushNotify } from 'components/Notifications';
import { useFormContext } from 'react-hook-form';
import { parseStringToNumber } from '../../../../../../../utils/functions/parseNumber';

export const CardsBlocks = (props: TCardsContentProps) => {
  const { currentId, setCurrentId } = props;
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [currentCard, setCurrentCard] = useState<CardsModel>();
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [pageSizeInput, setPageSizeInput] = useState<string>('10');
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const actionsMenuRef = useRef<HTMLDivElement | null>(null);
  const { data, isLoading } = useGetCardsQuery(
    { page, page_size: pageSize, search: search.trim() || undefined, active: activeFilter || undefined },
    {
      refetchOnMountOrArgChange: true,
    },
  );
  const [toggleCards, { isLoading: isToggleLoading }] = useToggleAllCardsMutation();
  const [resetCardIncome] = useResetCardIncomeMutation();
  const [resetCardsIncome, { isLoading: isResetLoading }] = useResetCardsIncomeMutation();
  const [applyCardsSettings, { isLoading: isApplyLoading }] = useApplyCardsSettingsMutation();
  const { getValues } = useFormContext();
  const cards = useMemo(() => mapCards(data?.results) || [], [data?.results]);
  const visibleIds = cards.map((card) => card.id);
  const totalPages = Math.ceil((data?.count || 0) / pageSize);
  const hasCards = Boolean(data?.total_count ?? data?.count ?? 0);
  const isActionsLoading = isToggleLoading || isResetLoading || isApplyLoading;

  const handleToggleVisible = async (status: boolean) => {
    if (!visibleIds.length) {
      return;
    }
    await toggleCards({ active: status, ids: visibleIds })
      .unwrap()
      .catch((error: any) => {
        pushNotify(error?.data?.detail || 'Не удалось изменить статус реквизитов', 'Ошибка');
      });
    setIsActionsOpen(false);
  };

  const handleToggleAll = async (status: boolean) => {
    await toggleCards({ active: status })
      .unwrap()
      .catch((error: any) => {
        pushNotify(error?.data?.detail || 'Не удалось изменить статус реквизитов', 'Ошибка');
      });
    setIsActionsOpen(false);
  };

  const handleResetVisible = async () => {
    if (!visibleIds.length) {
      return;
    }
    await resetCardsIncome({ ids: visibleIds })
      .unwrap()
      .catch((error: any) => {
        pushNotify(error?.data?.detail || 'Не удалось обнулить пополнения', 'Ошибка');
      });
    setIsActionsOpen(false);
  };

  const handleResetAll = async () => {
    await resetCardsIncome({})
      .unwrap()
      .catch((error: any) => {
        pushNotify(error?.data?.detail || 'Не удалось обнулить пополнения', 'Ошибка');
      });
    setIsActionsOpen(false);
  };

  const handleApplySettingsAll = async () => {
    if (!currentId) {
      return;
    }
    const currencyCode = currentCard?.currency_code;
    if (!currencyCode) {
      pushNotify('Не удалось определить валюту реквизита', 'Ошибка');
      return;
    }

    const values = getValues();
    await applyCardsSettings({
      currency_code: currencyCode,
      total_limit: parseStringToNumber(values.newLimit),
      min_income: +values.newMinIncome,
      max_income: +values.newMaxIncome,
      instant_active_orders: parseStringToNumber(values.newInstantOrders),
      daily_total_limit: parseStringToNumber(values.newDailyLimit || '0'),
      monthly_total_limit: parseStringToNumber(values.newMonthlyLimit || '0'),
      daily_limit_enabled: !!values.newDailyLimitEnabled,
      monthly_limit_enabled: !!values.newMonthlyLimitEnabled,
    })
      .unwrap()
      .catch((error: any) => {
        pushNotify(error?.data?.detail || 'Не удалось применить настройки ко всем реквизитам', 'Ошибка');
      });
    setIsActionsOpen(false);
  };

  useEffect(() => {
    setPage(1);
  }, [search, activeFilter, pageSize]);

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (!actionsMenuRef.current?.contains(event.target as Node)) {
        setIsActionsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, []);

  useEffect(() => {
    setPageSizeInput(String(pageSize));
  }, [pageSize]);

  const handleResetIncome = async (cardId: number) => {
    await resetCardIncome(cardId)
      .unwrap()
      .catch((error: any) => {
        pushNotify(error?.data?.detail || 'Не удалось обнулить пополнения', 'Ошибка');
      });
  };

  if (isLoading) {
    return <Loading />;
  }
  return (
    <div>
      <div className='cards_toolbar'>
        <div className='cards_stats'>
          <span>Всего: {data?.total_count ?? data?.count ?? 0}</span>
          <span>Включено: {data?.active_count ?? 0}</span>
          <span>Выключено: {data?.inactive_count ?? 0}</span>
        </div>
        <div className='cards_controls'>
          <input
            className='cards_search'
            type='search'
            placeholder='Поиск по названию'
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
              }
            }}
          />
          <select
            className='cards_select'
            value={activeFilter}
            onChange={(event) => setActiveFilter(event.target.value)}
          >
            <option value=''>Все</option>
            <option value='true'>Включенные</option>
            <option value='false'>Выключенные</option>
          </select>
          <input
            className='cards_select'
            type='number'
            min={1}
            max={500}
            value={pageSizeInput}
            onChange={(event) => setPageSizeInput(event.target.value)}
            onBlur={() => {
              const parsed = Number(pageSizeInput);
              const nextValue = Number.isFinite(parsed) ? Math.min(Math.max(parsed, 1), 500) : 10;
              setPageSize(nextValue);
              setPageSizeInput(String(nextValue));
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                const parsed = Number(pageSizeInput);
                const nextValue = Number.isFinite(parsed) ? Math.min(Math.max(parsed, 1), 500) : 10;
                setPageSize(nextValue);
                setPageSizeInput(String(nextValue));
              }
            }}
          />
          <div className='cards_actions' ref={actionsMenuRef}>
            <button
              type='button'
              className='cards_actions_button'
              disabled={isActionsLoading}
              onClick={() => setIsActionsOpen(!isActionsOpen)}
            >
              Действия
            </button>
            {isActionsOpen && (
              <div className='cards_actions_menu'>
                <div className='cards_actions_group'>Страница</div>
                <button
                  type='button'
                  className='cards_actions_item'
                  disabled={!visibleIds.length || isActionsLoading}
                  onClick={() => handleToggleVisible(true)}
                >
                  Включить страницу
                </button>
                <button
                  type='button'
                  className='cards_actions_item'
                  disabled={!visibleIds.length || isActionsLoading}
                  onClick={() => handleToggleVisible(false)}
                >
                  Выключить страницу
                </button>
                <button
                  type='button'
                  className='cards_actions_item warning'
                  disabled={!visibleIds.length || isActionsLoading}
                  onClick={handleResetVisible}
                >
                  Обнулить страницу
                </button>
                <div className='cards_actions_group'>Все реквизиты</div>
                <button
                  type='button'
                  className='cards_actions_item'
                  disabled={!hasCards || isActionsLoading}
                  onClick={() => handleToggleAll(true)}
                >
                  Включить все
                </button>
                <button
                  type='button'
                  className='cards_actions_item'
                  disabled={!hasCards || isActionsLoading}
                  onClick={() => handleToggleAll(false)}
                >
                  Выключить все
                </button>
                <button
                  type='button'
                  className='cards_actions_item warning'
                  disabled={!hasCards || isActionsLoading}
                  onClick={handleResetAll}
                >
                  Обнулить все
                </button>
                <div className='cards_actions_group'>Редактирование</div>
                <button
                  type='button'
                  className='cards_actions_item accent'
                  disabled={!currentId || !hasCards || isActionsLoading}
                  onClick={handleApplySettingsAll}
                >
                  Применить настройки ко всем
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <Blocks
        blocks={
          cards.map((card: CardsModel) => {
            return (
              <div key={card.id}>
                <div className={'block'}>
                  <div className={'component'}>
                    <p className={'block-component'}>Активность:</p>
                    <p className={'block-value'}>{card.active ? 'Активна' : 'Неактивна'}</p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Название реквизита:</p>
                    <p className={'block-value'}>{card.tag}</p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Описание:</p>
                    <p className={'block-value'}>{card.description}</p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Метод оплаты:</p>
                    <p className={'block-value'}>{card.payment_type}</p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Номер карты:</p>
                    <p className={'block-value'}>
                      {card.payment_type === 'C2C'  || card.payment_type === 'SBP' || card.payment_type === 'C2C,SBP'? card.account_number : <>–</>}
                    </p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Номер счета:</p>
                    <p className={'block-value'}>
                      {card.payment_type === 'SBP' || card.payment_type === 'TRANSFER' ? (
                        card.account_number
                      ) : (
                        <>–</>
                      )}
                    </p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>БИК</p>
                    <p className={'block-value'}>{card.bank.bic ?? '-'}</p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Банк:</p>
                    <p className={'block-value'}>{card.bank.name}</p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Назначение платежа</p>
                    <p className={'block-value'}>
                      {card.payment_type === 'TRANSFER' ? 'Перевод' : '-'}
                    </p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Номер телефона:</p>
                    <p className={'block-value'}>{card.phone_number}</p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Общий лимит:</p>
                    <p className={'block-value'}>{card.total_limit}</p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Всего пополнений:</p>
                    <p className={'block-value'}>{card.total_income}</p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Сброс пополнений:</p>
                    <button
                      type='button'
                      style={{ padding: '6px 10px' }}
                      onClick={() => handleResetIncome(card.id)}
                    >
                      Обнулить
                    </button>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Минимальная сумма платежа:</p>
                    <p className={'block-value'}>{card.min_income}</p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Максимальная сумма платежа:</p>
                    <p className={'block-value'}>{card.max_income}</p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Заявок одновременно не более:</p>
                    <p className={'block-value'}>{card.instant_active_orders}</p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Дневной лимит:</p>
                    <p className={'block-value'}>
                      {card.daily_limit_enabled ? String(card.daily_total_limit || '0') : 'Выкл'}
                    </p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Месячный лимит:</p>
                    <p className={'block-value'}>
                      {card.monthly_limit_enabled ? String(card.monthly_total_limit || '0') : 'Выкл'}
                    </p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>ФИО:</p>
                    <p className={'block-value'}>{card.fio}</p>
                  </div>
                  <div className={'block-buttons'}>
                    <div
                      className={'button-component'}
                      style={{ color: '#1890FF', marginTop: '6px', cursor: 'pointer' }}
                      onClick={() => {
                        setCurrentId(card.id);
                        setCurrentCard(card);
                        setModalOpen(true);
                      }}
                    >
                      <img src={'/edit_icon.png'} alt={'edit_icon'} />
                      Изменить
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        }
      />
      <EditCardModal open={modalOpen} setOpen={setModalOpen} card={currentCard} />
      <Pagination
        totalPages={totalPages}
        setCurrentPage={setPage}
        currentPage={page}
        nextPage={data?.next || page}
        previousPage={data?.previous || page}
      />
    </div>
  );
};
